import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    MessageSquareText,
    Phone,
    Disc3,
    Gauge,
    Wrench,
    Cog,
    CheckCircle2,
    Clock,
    Info,
    ArrowRight,
} from "lucide-react";
import {
    SMS_HREF,
    TEL_HREF,
    PHONE_DISPLAY,
    PHONE_E164,
    SERVICES,
    SERVICE_CATALOG,
    SERVICE_CATEGORIES,
} from "@/lib/site";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { useServices, useServiceCatalog, useSiteText } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

const ICONS = {
    brakes: Disc3,
    suspension: Gauge,
    diagnostics: Wrench,
    installs: Cog,
};

const HIGHLIGHTS = {
    brakes: [
        "Pad + rotor swaps",
        "Caliper replacement",
        "Performance brake kits",
        "Brake fluid flush",
    ],
    suspension: [
        "Full coilover install",
        "Lowering springs",
        "Control arms & bushings",
        "Ride-height tuning",
    ],
    diagnostics: [
        "Check engine light scan",
        "Misfire & noise tracing",
        "Electrical troubleshooting",
        "Clear, honest report",
    ],
    installs: [
        "Intakes & exhausts",
        "Spark plugs & coils",
        "Fluid & belt services",
        "Bolt-on performance parts",
    ],
};

export default function Services() {
    const navigate = useNavigate();
    const [active, setActive] = useState("All");
    const services = useServices();
    const serviceCatalog = useServiceCatalog();
    const phone = useSiteText("business.phone_display", PHONE_DISPLAY);

    useSEO({
        title:
            "Services & Pricing — Mobile Mechanic, Hampton Roads (Brakes, Coilovers, Diagnostics, Installs)",
        description:
            "Mobile mechanic services + transparent starting pricing in Virginia Beach, Norfolk, Chesapeake & Portsmouth. Brakes, suspension, diagnostics, performance installs, tuning & mods.",
        image: services[0]?.image,
    });

    const groupedItems = useMemo(() => {
        if (active !== "All") return null;
        const groups = {};
        for (const svc of serviceCatalog) {
            if (!groups[svc.category]) groups[svc.category] = [];
            groups[svc.category].push(svc);
        }
        return SERVICE_CATEGORIES.filter((c) => c !== "All" && groups[c]).map(
            (c) => ({ category: c, items: groups[c] })
        );
    }, [active, serviceCatalog]);

    const flatItems = useMemo(
        () => serviceCatalog.filter((s) => s.category === active),
        [active, serviceCatalog]
    );

    const onRequest = (svc) => {
        navigate(`/contact?service=${encodeURIComponent(svc.title)}`);
    };

    return (
        <div data-testid="services-page">
            <Breadcrumb section="Services & Pricing" />

            {/* HERO */}
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-14 sm:pb-20 lg:pb-24">
                    <EditableText
                        path="services.page.eyebrow"
                        defaultValue="What I do · Hampton Roads mobile mechanic"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="services.page.title"
                        defaultValue="Services & Pricing"
                        as="h1"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="services-hero-title"
                    />
                    <EditableText
                        path="services.page.subtitle"
                        defaultValue="Straightforward mobile work across Hampton Roads. The four lanes below cover most jobs — scroll down for starting prices on every common service. Final pricing depends on your vehicle, parts, and condition."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />
                </div>
            </section>

            {/* HIGH-LEVEL SERVICE CARDS */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-12 sm:py-16 space-y-4">
                    {services.map((svc, idx) => {
                        const Icon = ICONS[svc.slug] || Wrench;
                        const reverse = idx % 2 === 1;
                        return (
                            <article
                                key={svc.slug}
                                data-testid={`service-card-${svc.slug}`}
                                className="grid lg:grid-cols-5 gap-0 overflow-hidden rounded-sm border border-white/10 bg-zinc-900"
                            >
                                <div
                                    className={`lg:col-span-2 relative aspect-[4/3] lg:aspect-auto ${
                                        reverse ? "lg:order-2" : ""
                                    }`}
                                >
                                    <img
                                        src={svc.image}
                                        alt={svc.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                </div>
                                <div className="lg:col-span-3 p-6 sm:p-10 flex flex-col justify-center">
                                    <div className="flex items-center gap-3">
                                        <span className="inline-flex w-10 h-10 items-center justify-center rounded-sm bg-red-600/15 border border-red-600/30">
                                            <Icon className="w-5 h-5 text-red-500" />
                                        </span>
                                        <EditableText
                                            path={`services.${svc.slug}.title`}
                                            defaultValue={svc.title}
                                            as="h2"
                                            className="font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl"
                                        />
                                    </div>
                                    <EditableText
                                        path={`services.${svc.slug}.long`}
                                        defaultValue={svc.long}
                                        multiline
                                        as="p"
                                        className="mt-5 text-zinc-400 leading-relaxed max-w-2xl"
                                    />

                                    <ul className="mt-6 grid sm:grid-cols-2 gap-2">
                                        {(HIGHLIGHTS[svc.slug] || []).map(
                                            (h, hi) => (
                                                <li
                                                    key={hi}
                                                    className="inline-flex items-center gap-2 text-sm text-zinc-300"
                                                >
                                                    <CheckCircle2 className="w-4 h-4 text-red-500" />
                                                    <EditableText
                                                        path={`services.${svc.slug}.highlights.${hi}`}
                                                        defaultValue={h}
                                                        as="span"
                                                    />
                                                </li>
                                            )
                                        )}
                                    </ul>

                                    <div className="mt-7 flex flex-wrap gap-3">
                                        <a
                                            href={SMS_HREF}
                                            data-testid={`service-text-button-${svc.slug}`}
                                            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 transition-colors text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                        >
                                            <MessageSquareText className="w-4 h-4" />
                                            Text For Quote
                                        </a>
                                        <a
                                            href="#pricing"
                                            data-testid={`service-pricing-link-${svc.slug}`}
                                            className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                        >
                                            See Pricing
                                        </a>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* PRICING CATALOG */}
            <section id="pricing" className="bg-black border-b border-white/10 scroll-mt-24">
                <div className="lbp-container pt-14 sm:pt-20 pb-2">
                    <EditableText
                        path="services.pricing.eyebrow"
                        defaultValue="Transparent pricing"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="services.pricing.title"
                        defaultValue="Starting Prices"
                        as="h2"
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl"
                        data-testid="pricing-section-title"
                    />
                    <EditableText
                        path="services.pricing.subtitle"
                        defaultValue="Real numbers for the jobs I do most. Every price below is a starting estimate — text for an exact quote in 5 minutes."
                        multiline
                        as="p"
                        className="mt-4 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                    />

                    <div
                        className="mt-6 inline-flex items-start gap-3 rounded-sm border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-200 max-w-2xl"
                        data-testid="pricing-disclaimer"
                    >
                        <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-400" />
                        <p className="text-sm leading-relaxed">
                            <span className="font-bold">Not a final price.</span>{" "}
                            Numbers below are starting estimates only. Final
                            pricing varies by vehicle make/model, parts, and
                            condition. Always confirm via text first.
                        </p>
                    </div>
                </div>

                <div className="lbp-container pt-8 pb-16 sm:pb-20">
                    <div
                        className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0"
                        data-testid="pricing-categories"
                    >
                        {SERVICE_CATEGORIES.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setActive(cat)}
                                data-testid={`pricing-category-${cat
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className={`px-4 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm border whitespace-nowrap transition-colors ${
                                    active === cat
                                        ? "bg-red-600 border-red-600 text-white"
                                        : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                }`}
                            >
                                {cat === "All" ? "All Services" : cat}
                            </button>
                        ))}
                    </div>

                    {groupedItems ? (
                        <div className="mt-10 space-y-12">
                            {groupedItems.map(({ category, items }) => (
                                <div
                                    key={category}
                                    data-testid={`pricing-group-${category
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                >
                                    <div className="flex items-baseline justify-between border-b border-white/10 pb-3 mb-5">
                                        <h3 className="font-display font-black uppercase tracking-tight text-white text-xl sm:text-2xl">
                                            {category}
                                        </h3>
                                        <span className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                                            {items.length}{" "}
                                            {items.length === 1
                                                ? "service"
                                                : "services"}
                                        </span>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                        {items.map((svc) => (
                                            <PricingCard
                                                key={svc.slug}
                                                svc={svc}
                                                onRequest={onRequest}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {flatItems.map((svc) => (
                                <PricingCard
                                    key={svc.slug}
                                    svc={svc}
                                    onRequest={onRequest}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-black">
                <div className="lbp-container py-16 sm:py-20 text-center">
                    <EditableText
                        path="services.final_cta.title"
                        defaultValue="Don't See It? Still Ask."
                        as="h2"
                        className="font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl max-w-2xl mx-auto"
                    />
                    <EditableText
                        path="services.final_cta.body"
                        defaultValue="If it rolls, bolts, or wrenches — message me. I'll tell you straight if it's something I can handle mobile."
                        multiline
                        as="p"
                        className="mt-4 text-zinc-400 max-w-xl mx-auto"
                    />
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <a
                            href={SMS_HREF}
                            data-testid="services-cta-text"
                            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                        >
                            <MessageSquareText className="w-5 h-5" />
                            Text {phone}
                        </a>
                        <a
                            href={TEL_HREF}
                            data-testid="services-cta-call"
                            className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-7 py-4 rounded-sm font-display font-bold uppercase tracking-wide text-sm sm:text-base"
                        >
                            <Phone className="w-5 h-5" />
                            Call
                        </a>
                        <Link
                            to="/contact"
                            data-testid="services-cta-form"
                            className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-7 py-4 rounded-sm font-display font-bold uppercase tracking-wide text-sm sm:text-base"
                        >
                            Send Form
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function PricingCard({ svc, onRequest }) {
    return (
        <article
            data-testid={`pricing-card-${svc.slug}`}
            className="rounded-sm border border-white/10 bg-zinc-900 overflow-hidden flex flex-col group hover:border-red-600/40 transition-colors"
        >
            <div className="relative aspect-[4/3] overflow-hidden">
                <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                <span className="absolute top-3 left-3 inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-black/70 text-zinc-200 border border-white/10 backdrop-blur-sm">
                    {svc.category}
                </span>
            </div>
            <div className="p-5 flex flex-col flex-1">
                <EditableText
                    path={`catalog.${svc.slug}.title`}
                    defaultValue={svc.title}
                    as="h3"
                    className="font-display font-bold uppercase tracking-tight text-white text-lg leading-snug"
                />
                <div className="mt-3 flex items-center justify-between text-sm border-t border-white/5 pt-3">
                    <span className="inline-flex items-center gap-1.5 text-zinc-400">
                        <Clock className="w-3.5 h-3.5 text-red-500" />
                        <EditableText
                            path={`catalog.${svc.slug}.duration`}
                            defaultValue={svc.duration}
                            as="span"
                        />
                    </span>
                    <EditableText
                        path={`catalog.${svc.slug}.price`}
                        defaultValue={svc.price}
                        as="span"
                        className="font-display font-bold text-white"
                    />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => onRequest(svc)}
                        data-testid={`pricing-request-${svc.slug}`}
                        className="inline-flex items-center justify-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2.5 rounded-sm font-display font-bold uppercase tracking-wide text-xs"
                    >
                        Request <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <a
                        href={`sms:${PHONE_E164}?&body=${encodeURIComponent(
                            `Hey — interested in ${svc.title}. My vehicle is `
                        )}`}
                        data-testid={`pricing-text-${svc.slug}`}
                        className="inline-flex items-center justify-center gap-1 border border-white/15 hover:border-white text-white px-3 py-2.5 rounded-sm font-display font-bold uppercase tracking-wide text-xs"
                    >
                        <MessageSquareText className="w-3.5 h-3.5" />
                        Text
                    </a>
                </div>
            </div>
        </article>
    );
}
